import {
  useBrokersServiceBrokersBrokersProductsBulkUpdateCreate,
  useBrokersServiceBrokersBrokersProductsList,
  useSalesServiceSalesProductsList
} from '@insurance/api/queries'
import { BrokerProductBulkUpdate, ProductList } from '@insurance/api/requests'
import { ProductSelect } from '@insurance/components'
import { ProductCode } from '@insurance/types'
import { showApiErrors, validateCommission } from '@shared/utils'
import {
  App,
  Button,
  Card,
  Col,
  Flex,
  Form,
  FormListFieldData,
  FormProps,
  Input,
  Row
} from 'antd'
import { Add, Trash } from 'iconsax-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ProductsConfigParams } from './ProductsConfigParams'

type FormValues = {
  products: Array<{
    product: number
    commission: string
    config?: {
      coefficient_min: string
      coefficient_max: string
      coefficient_default: string
    }
  }>
}

/**
 * The per-product settings sub-form. Split out of the row so resolving the
 * product code does not add two more levels of nesting inside the field list.
 */
const ProductConfigFields = ({
  field,
  products
}: {
  field: FormListFieldData
  products?: ProductList[]
}) => {
  const productId = Form.useWatch(['products', field.name, 'product'])

  const code = products?.find(
    (product) => String(product.id) === String(productId)
  )?.code

  return (
    <ProductsConfigParams
      code={code as ProductCode}
      componentProps={{ field }}
    />
  )
}

type Props = {
  companyId?: number
  onNextStep?: () => void
} & FormProps

export const CompaniesProductsForm = ({
  companyId,
  onNextStep,
  ...props
}: Props) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm()

  const { data: productsAvailable } = useSalesServiceSalesProductsList({
    availableToBrokers: true
  })

  const { data: products } = useBrokersServiceBrokersBrokersProductsList(
    {
      id: Number(companyId)
    },
    [companyId],
    {
      enabled: !!companyId
    }
  )

  useEffect(() => {
    if (products?.length) {
      const formData = {
        products: products?.map((product) => ({
          product: String(product.product?.id),
          commission: product.commission,
          config: product.config
        }))
      }
      form.setFieldsValue(formData)
    } else {
      form.setFieldsValue({
        products: [{ product: undefined, commission: '', key: '100' }]
      })
    }
  }, [products, form])

  const { mutate: createMutation } =
    useBrokersServiceBrokersBrokersProductsBulkUpdateCreate({
      onSuccess: () => {
        message.success(t('products_updated'))
        onNextStep?.()
      },
      onError: (error) => showApiErrors(error, form)
    })

  const onFinish = (values: FormValues) => {
    if (companyId && values.products) {
      const bulkUpdateData: BrokerProductBulkUpdate = {
        products: values.products.map((item) => ({
          product: Number(item.product),
          commission: item.commission,
          ...(item.config && { config: item.config })
        }))
      }
      createMutation({
        id: companyId,
        requestBody: bulkUpdateData
      })
    }
  }

  const productsExcludesIds = products?.map((product) =>
    String(product.product?.id)
  )

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} {...props}>
      <Form.List name="products">
        {(fields, { add, remove }) => {
          return (
            <Flex vertical gap={16}>
              {fields?.map((field) => (
                <Card
                  key={field.key}
                  size="small"
                  style={{ paddingBottom: 16 }}
                >
                  <Row gutter={24} align="middle">
                    <Col span={11}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'product']}
                        label={t('product')}
                        rules={[{ required: true }]}
                      >
                        <ProductSelect
                          availableToBrokers
                          defaultValue={String(
                            products?.[field?.key]?.product?.id
                          )}
                          excludeIds={productsExcludesIds}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={11}>
                      <Form.Item
                        {...field}
                        name={[field.name, 'commission']}
                        label={t('commission')}
                        rules={[
                          {
                            required: true
                          },
                          {
                            validator: (_, value) =>
                              validateCommission(value, t)
                          }
                        ]}
                      >
                        <Input
                          type="number"
                          placeholder={t('commission_placeholder')}
                          min={0}
                          step={0.01}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={2} style={{ marginTop: 6 }}>
                      <Form.Item noStyle>
                        <Button
                          danger
                          icon={<Trash size={16} />}
                          disabled={fields?.length === 1}
                          onClick={() => remove(field.name)}
                        />
                      </Form.Item>
                    </Col>

                    <ProductConfigFields
                      field={field}
                      products={productsAvailable?.results}
                    />
                  </Row>
                </Card>
              ))}

              <Flex justify="end">
                <Button
                  type="dashed"
                  onClick={() => add()}
                  disabled={fields?.length === productsAvailable?.count}
                  icon={<Add size={16} />}
                >
                  {t('add_product')}
                </Button>
              </Flex>
            </Flex>
          )
        }}
      </Form.List>
    </Form>
  )
}
